import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { EBTWICBanner } from '../components/layout/EBTWICBanner';
import { ProductCard } from '../components/product/ProductCard';
import { EBTFilter } from '../components/ui/EBTFilter';
import { mockProducts } from '../data/mockProducts';
export function SearchPage() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [ebtOnly, setEbtOnly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // Handle URL params for category and search
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const queryParam = searchParams.get('q');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
    if (queryParam) {
      setQuery(queryParam);
    }
  }, [searchParams]);
  const categories = Array.from(new Set(mockProducts.map(p => p.category)));
  const filteredProducts = useMemo(() => {
    return mockProducts.filter(product => {
      const matchesQuery = product.name.toLowerCase().includes(query.toLowerCase()) || product.category.toLowerCase().includes(query.toLowerCase());
      const matchesEbt = ebtOnly ? product.isEBT : true;
      const matchesCategory = selectedCategory ? product.category === selectedCategory : true;
      return matchesQuery && matchesEbt && matchesCategory;
    });
  }, [query, ebtOnly, selectedCategory]);
  return <div className="min-h-screen bg-[#FAF8F5]" data-id="element-371">
      <EBTWICBanner data-id="element-372" />
      <TopNav data-id="element-373" />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 pb-24 md:pb-12" data-id="element-374">
        {/* Header */}
        <div className="mb-6 md:mb-8" data-id="element-375">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6" data-id="element-376">
            Shop All Products
          </h1>

          {/* Search and Filter Row */}
          <div className="flex flex-col lg:flex-row gap-4" data-id="element-377">
            <div className="flex-grow relative" data-id="element-378">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-379" />
              <input type="text" placeholder="Search for yams, spices, diapers..." value={query} onChange={e => setQuery(e.target.value)} className="w-full bg-white border border-gray-200 rounded-xl py-3 md:py-4 pl-12 pr-4 text-base focus:ring-2 focus:ring-[#D97757] focus:border-transparent transition-all shadow-sm" data-id="element-380" />
            </div>
            <div className="lg:w-80" data-id="element-381">
              <EBTFilter isEnabled={ebtOnly} onToggle={setEbtOnly} data-id="element-382" />
            </div>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex gap-2 md:gap-3 mb-6 md:mb-8 overflow-x-auto pb-2 no-scrollbar" data-id="element-383">
          <button onClick={() => setSelectedCategory(null)} className={`px-4 md:px-6 py-2 md:py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${selectedCategory === null ? 'bg-[#1E3A5F] text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-384">
            All Items
          </button>
          {categories.map(cat => <button key={cat} onClick={() => setSelectedCategory(cat === selectedCategory ? null : cat)} className={`px-4 md:px-6 py-2 md:py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${selectedCategory === cat ? 'bg-[#1E3A5F] text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-385">
              {cat}
            </button>)}
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-4 md:mb-6" data-id="element-386">
          <h2 className="text-lg md:text-xl font-bold text-gray-900" data-id="element-387">
            {filteredProducts.length}{' '}
            {filteredProducts.length === 1 ? 'Product' : 'Products'}
          </h2>
          {(query || ebtOnly || selectedCategory) && <button onClick={() => {
          setQuery('');
          setEbtOnly(false);
          setSelectedCategory(null);
        }} className="text-sm text-[#D97757] font-semibold hover:underline" data-id="element-388">
              Clear All Filters
            </button>}
        </div>

        {/* Product Grid */}
        {filteredProducts.length > 0 ? <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-389">
            {filteredProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-390" />)}
          </div> : <div className="flex flex-col items-center justify-center py-16 md:py-20 text-center" data-id="element-391">
            <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6 text-3xl md:text-4xl" data-id="element-392">
              🔍
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3" data-id="element-393">
              No products found
            </h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6 md:mb-8 text-sm md:text-base" data-id="element-394">
              We couldn't find any items matching your filters. Try adjusting
              your search or turning off the EBT filter.
            </p>
            <button onClick={() => {
          setQuery('');
          setEbtOnly(false);
          setSelectedCategory(null);
        }} className="bg-[#D97757] text-white px-6 md:px-8 py-3 rounded-xl font-bold hover:bg-[#c66545] transition-colors" data-id="element-395">
              Show All Products
            </button>
          </div>}
      </main>

      <BottomNav data-id="element-396" />
    </div>;
}